import { isEN } from "@/utils/i18n";
import React from "react";
import { useTranslation } from "react-i18next";

const PrivacyPolicy = () => {
  const { i18n } = useTranslation();
  if (!isEN(i18n)) {
    return (
      <div className="max-w-3xl mx-auto h-fit bg-white p-8 rounded-lg shadow-lg">
        <h1 className="text-3xl font-bold text-blue-700 mb-6">隱私政策</h1>
        <p className="mb-4">
          本應用重視用戶的個人資料私隱，並承諾根據《個人資料（私隱）條例》及相關法例，保障閣下的資料安全。請仔細閱讀以下政策內容。
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">1. 收集的資料類型</h2>
        <ul className="list-disc list-inside mb-4 space-y-1">
          <li>用戶姓名、學號（如有）、班級、學校名稱</li>
          <li>聯絡資料（如電郵地址）</li>
          <li>作業內容及提交記錄</li>
          <li>題目及答案資料</li>
          <li>登入及使用記錄</li>
        </ul>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">2. 資料用途</h2>
        <ul className="list-disc list-inside mb-4 space-y-1">
          <li>用於註冊及管理帳戶</li>
          <li>協助老師佈置及批改作業</li>
          <li>學習進度分析與報告</li>
          <li>針對學生能力的個性化學習</li>
        </ul>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">3. 資料保存及安全</h2>
        <p className="mb-4">
          我們會將您的個人資料儲存於受保護的伺服器，僅授權人員可查閱。資料只會保存至達成收集目的所需的最短時間，並會定期刪除或匿名化。
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">4. 資料披露與分享</h2>
        <p className="mb-4">
          除非法律要求，或獲閣下（及家長／監護人）同意，我們不會向第三方披露您的個人資料。部分資料會因應資料所有者或學校或教育局要求，在合法範圍內進行分享。
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">5. 未成年人私隱</h2>
        <p className="mb-4">
          若用戶為18歲以下，須經家長或監護人同意後方可使用本應用。如有需要，可聯絡我們查閱、更正或刪除子女的個人資料。
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">6. AI相關功能私隱</h2>
        <p className="mb-4">
          本應用僅會將題目、答案傳輸至三方AI平台，用戶請勿在與AI對話中輸入身份證號碼、住址、聯絡電話、銀行賬戶等敏感個人資料。
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">7. 用戶權利</h2>
        <p className="mb-4">
          閣下有權查閱、更正或要求刪除您的個人資料。如有相關查詢，請電郵至{" "}
          <a href="mailto:easeplatform@eduhk.hk" className="text-blue-500 underline">easeplatform@eduhk.hk</a>。
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">8. Cookies 及追蹤技術</h2>
        <p className="mb-4">
          本應用可能會使用Cookies及相關技術提升用戶體驗。閣下可於瀏覽器中禁用Cookies，但部分功能或會受影響。
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">9. 政策更新</h2>
        <p className="mb-4">
          若本政策有任何更改，我們會於應用內公告或以電郵通知用戶。請定期查閱本頁以獲取最新資訊。
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">10. 聯絡我們</h2>
        <p className="mb-4">
          如對本隱私政策有任何疑問，歡迎聯絡：{" "}
          <a href="mailto:easeplatform@eduhk.hk" className="text-blue-500 underline">easeplatform@eduhk.hk</a>
        </p>

        <p className="text-gray-500 text-sm mt-8">最後更新日期：2025年6月24日</p>
      </div>
    )
  } else {
    (
      <div className="max-w-3xl mx-auto h-fit bg-white p-8 rounded-lg shadow-lg">
        <h1 className="text-3xl font-bold text-blue-700 mb-6">Privacy Policy</h1>
        <p className="mb-4">
          This application values user privacy and is committed to protecting your data in accordance with the Personal Data (Privacy) Ordinance and related laws. Please read the following policy carefully.
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">1. Types of Collected Data</h2>
        <ul className="list-disc list-inside mb-4 space-y-1">
          <li>User name, student ID (if applicable), class, and school name</li>
          <li>Contact information (such as email address)</li>
          <li>Homework content and submission records</li>
          <li>Question and answer data</li>
          <li>Login and usage logs</li>
        </ul>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">2. Data Usage</h2>
        <ul className="list-disc list-inside mb-4 space-y-1">
          <li>Account registration and management</li>
          <li>Assisting teachers in assigning and grading homework</li>
          <li>Learning progress analysis and reporting</li>
          <li>Personalized learning based on student abilities</li>
        </ul>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">3. Data Storage and Security</h2>
        <p className="mb-4">
          Your personal data will be stored on protected servers with access restricted to authorized personnel only. Data will be retained only for the minimum period necessary to achieve collection purposes and will be regularly deleted or anonymized.
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">4. Data Disclosure and Sharing</h2>
        <p className="mb-4">
          We will not disclose your personal data to third parties unless required by law or with your (and parent/guardian's) consent. Some data may be shared within legal boundaries at the request of data owners, schools, or educational authorities.
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">5. Minor Privacy</h2>
        <p className="mb-4">
          Users under 18 years of age must obtain parental or guardian consent before using this application. Upon request, parents/guardians may contact us to access, correct, or delete their child's personal data.
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">6. AI Function Privacy</h2>
        <p className="mb-4">
          This application will only transmit questions and answers to third-party AI platforms. Users should not input sensitive personal information such as ID numbers, addresses, phone numbers, or bank account details in AI conversations.
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">7. User Rights</h2>
        <p className="mb-4">
          You have the right to access, correct, or request deletion of your personal data. For related inquiries, please email us at{" "}
          <a href="mailto:easeplatform@eduhk.hk" className="text-blue-500 underline">easeplatform@eduhk.hk</a>.
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">8. Cookies and Tracking Technologies</h2>
        <p className="mb-4">
          This application may use cookies and related technologies to enhance user experience. You may disable cookies in your browser, though some functions may be affected.
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">9. Policy Updates</h2>
        <p className="mb-4">
          If this policy changes, we will announce updates in the application or notify users via email. Please check this page regularly for the latest information.
        </p>

        <h2 className="text-xl font-semibold text-blue-600 mt-8 mb-2">10. Contact Us</h2>
        <p className="mb-4">
          For any questions regarding this privacy policy, please contact us at:{" "}
          <a href="mailto:easeplatform@eduhk.hk" className="text-blue-500 underline">easeplatform@eduhk.hk</a>
        </p>

        <p className="text-gray-500 text-sm mt-8">Last updated: June 24, 2025</p>
      </div>
    );
  }
};

export default PrivacyPolicy;